// Pass 2.15 — confirmation dialog for hard-deleting a workspace.
//
// Phase 6.3.2 (B.1): chrome is now the <Modal> primitive (backdrop,
// focus trap, Esc, focus return, busy-locks-dismissal). This file
// owns only the confirmation copy + the confirm/cancel buttons and
// the in-flight error handling.

import { useCallback, useRef, useState } from "react";
import { Loader2 } from "lucide-react";

import { Modal } from "./ui/Modal";

interface DeleteWorkspaceDialogProps {
  workspaceName: string;
  /** Surfaced in the dialog body so the user understands the
   *  magnitude of the operation. Pass null when unknown — the
   *  dialog falls back to a generic phrasing. */
  conversationCount: number | null;
  /** Called when the user confirms. Parent should perform the
   *  delete + navigate; the dialog manages loading/error state. */
  onConfirm: () => Promise<void>;
  /** Called on Cancel, Esc, or backdrop click (NOT while busy). */
  onClose: () => void;
}

export function DeleteWorkspaceDialog({
  workspaceName,
  conversationCount,
  onConfirm,
  onClose,
}: DeleteWorkspaceDialogProps) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const confirmRef = useRef<HTMLButtonElement | null>(null);

  const handleConfirm = useCallback(async () => {
    if (busy) return;
    setBusy(true);
    setError(null);
    try {
      await onConfirm();
      // On success the parent unmounts the dialog (state change).
    } catch (e) {
      setBusy(false);
      const msg = e instanceof Error ? e.message : "Delete failed";
      setError(`Delete failed: ${msg}`);
      confirmRef.current?.focus();
    }
  }, [busy, onConfirm]);

  const bodyText =
    conversationCount !== null
      ? `${conversationCount} conversation${conversationCount === 1 ? "" : "s"} will be permanently deleted.`
      : null;

  return (
    <Modal open onClose={onClose} title="Delete workspace?" size="sm" busy={busy}>
      <p className="text-body-sm mb-snug">
        This will permanently delete your entire archive of{" "}
        <span className="text-theme-text-primary">{workspaceName}</span> — all
        messages, attachments, Drive copies, and the saved Slack session.
        Anything Slack no longer serves cannot be re-archived. This cannot be
        undone.
      </p>
      {bodyText && (
        <p data-testid="delete-workspace-dialog-counts" className="text-caption mb-loose">
          {bodyText}
        </p>
      )}
      {error && (
        <p
          data-testid="delete-workspace-dialog-error"
          role="alert"
          className="text-body-sm text-theme-error-text bg-theme-error-bg border border-theme-error-border px-3 py-2 mb-loose leading-relaxed"
        >
          {error}
        </p>
      )}
      <div className="flex justify-end gap-snug">
        <button
          type="button"
          onClick={onClose}
          disabled={busy}
          data-testid="delete-workspace-dialog-cancel"
          className="px-3 py-1.5 border border-theme-border-default text-caption text-theme-text-secondary hover:text-theme-text-primary hover:border-theme-border-emphasis transition-colors duration-150 disabled:opacity-50"
        >
          Cancel
        </button>
        <button
          ref={confirmRef}
          type="button"
          onClick={handleConfirm}
          disabled={busy}
          data-testid="delete-workspace-dialog-confirm"
          className="inline-flex items-center gap-tight px-3 py-1.5 border border-theme-error-border text-caption text-theme-error-text hover:bg-theme-error-bg transition-colors duration-150 disabled:opacity-50"
        >
          {busy && <Loader2 className="w-3 h-3 animate-spin" strokeWidth={1.5} aria-hidden />}
          Delete forever
        </button>
      </div>
    </Modal>
  );
}
